import useSWR, { mutate } from 'swr';
import {
  getDailyActivitiesWithAssignments,
  GetDailyActivitiesParams,
  DailyActivityListResponse,
} from '@/features/projects/api';
import { DailyActivityWithAssignments } from '@/types';
import { useState, useCallback } from 'react';
import { toast } from "sonner";
import apiClient from '@/shared/api/client';

/**
 * 日次活動の返り値
 */
interface UseDailyActivitiesReturn {
  activities?: DailyActivityWithAssignments[];
  totalCount?: number;
  isLoading: boolean;
  error?: Error;
  filters: Pick<GetDailyActivitiesParams, 'start_date' | 'end_date'>; // Only expose date filters for now
  setFilters: (filters: Pick<GetDailyActivitiesParams, 'start_date' | 'end_date'>) => void;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
  };
  setPage: (page: number) => void;
  mutate: () => void; // SWR mutate
  // CRUD操作
  addActivity: (activityData: Record<string, unknown>) => Promise<boolean>;
  updateActivity: (id: string, updateData: Record<string, unknown>) => Promise<boolean>;
  deleteActivity: (id: string) => Promise<boolean>;
}

const DEFAULT_PAGE_SIZE = 30;

/**
 * 関連するキャッシュキーを取得
 */
const getRelatedCacheKeys = (): string[] => {
  return [
    '/daily-activities',
    '/projects',
    // REMOVED: project-material-usages excluded from GenbaFlow
  ];
};

/**
 * キャッシュを一括更新するヘルパー
 */
const updateRelatedCaches = async () => {
  const cacheKeys = getRelatedCacheKeys();
  await Promise.all(
    cacheKeys.map(pattern =>
      mutate(
        (key) => typeof key === 'string' && key.startsWith(pattern),
        undefined,
        { revalidate: true }
      )
    )
  );
};

/**
 * 日次活動を取得するフック
 * @param projectId プロジェクトID
 * @param initialStartDate 初期開始日
 * @param initialEndDate 初期終了日
 */
export const useDailyActivities = (
  projectId: string,
  initialStartDate: string,
  initialEndDate: string
): UseDailyActivitiesReturn => {
  const [filters, setFilters] = useState<Pick<GetDailyActivitiesParams, 'start_date' | 'end_date'>>({
    start_date: initialStartDate,
    end_date: initialEndDate,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;

  // 安定したSWRキーを生成
  const swrKey = projectId && filters.start_date && filters.end_date ? [
    `/projects/${projectId}/daily-activities`,
    JSON.stringify(filters),
    currentPage,
    pageSize,
  ] : null;

  // SWRのフェッチ関数
  const fetcher = async ([, stringifiedFilters, page, limit]: [string, string, number, number]) => {
    const parsedFilters = JSON.parse(stringifiedFilters);
    const params: GetDailyActivitiesParams = {
      ...parsedFilters,
      skip: (page - 1) * limit,
      limit: limit,
      include_assignments: true,
    };
    return getDailyActivitiesWithAssignments(projectId, params);
  };

  // SWRフック
  const { data, error, isLoading, mutate: swrMutate } = useSWR<DailyActivityListResponse>(
    swrKey,
    fetcher,
    { keepPreviousData: true }
  );

  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // フィルターを更新し、ページをリセット
  const handleSetFilters = (newFilters: Pick<GetDailyActivitiesParams, 'start_date' | 'end_date'>) => {
    if (newFilters.start_date && newFilters.end_date) {
      setFilters(newFilters);
      setCurrentPage(1);
    }
  };

  // 現在のページを安全に設定
  const setPage = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // CRUD操作
  const addActivity = useCallback(async (activityData: Record<string, unknown>): Promise<boolean> => {
    try {
      await apiClient.post('/daily-activities', activityData);

      // 関連するキャッシュを更新
      await updateRelatedCaches();

      toast.success("日次活動を追加しました");
      return true;
    } catch (error: unknown) {
      console.error('Error adding daily activity:', error);
      toast.error(`日次活動の追加に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
      return false;
    }
  }, []);

  const updateActivity = useCallback(async (id: string, updateData: Record<string, unknown>): Promise<boolean> => {
    try {
      await apiClient.patch(`/daily-activities/${id}`, updateData);

      // 関連するキャッシュを更新
      await updateRelatedCaches();

      toast.success("日次活動を更新しました");
      return true;
    } catch (error: unknown) {
      console.error('Error updating daily activity:', error);
      toast.error(`日次活動の更新に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
      return false;
    }
  }, []);

  const deleteActivity = useCallback(async (id: string): Promise<boolean> => {
    try {
      await apiClient.delete(`/daily-activities/${id}`);

      // 関連するキャッシュを更新
      await updateRelatedCaches();

      toast.success("日次活動を削除しました");
      return true;
    } catch (error: unknown) {
      console.error('Error deleting daily activity:', error);
      toast.error(`日次活動の削除に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
      return false;
    }
  }, []);

  return {
    activities: data?.items,
    totalCount,
    isLoading,
    error,
    filters,
    setFilters: handleSetFilters,
    pagination: {
      currentPage,
      pageSize,
      totalPages,
    },
    setPage,
    mutate: swrMutate,
    addActivity,
    updateActivity,
    deleteActivity,
  };
}; 